import { useCallback, useEffect, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, ApiError } from '@/lib/api';

/**
 * /consultant/protos — Sprint 2026-23 T-B7.
 *
 * Wedge-1 sales motion: консультант создаёт proto-tenants для farmers,
 * видит список своих proto-tenants («моих кандидатов»), может вручную
 * promote (для тестинга — в production farmer accept'ит magic-link).
 *
 * Low-fi: plain Tailwind, форма + список, никакой полировки —
 * Sprint 2026-24 hi-fi через Design-Lead.
 */

interface ProtoOut {
  id: string;
  slug: string;
  name: string;
  status: 'proto' | 'active';
  proto_owner_user_id: string | null;
  email_domain: string | null;
  created_at: string;
}

interface CreateForm {
  name: string;
  slug: string;
  email_domain: string;
}

const EMPTY_FORM: CreateForm = { name: '', slug: '', email_domain: '' };

function formatCreatedAt(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('ru-RU');
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: ProtoOut['status'] }) {
  const isActive = status === 'active';
  return (
    <span
      data-testid="status-badge"
      className={
        'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ' +
        (isActive
          ? 'bg-emerald-100 text-emerald-800'
          : 'bg-amber-100 text-amber-800')
      }
    >
      {isActive ? 'active' : 'proto'}
    </span>
  );
}

export function ConsultantProtosPage() {
  const [protos, setProtos] = useState<ProtoOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  const [form, setForm] = useState<CreateForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [promotingId, setPromotingId] = useState<string | null>(null);
  const [promoteError, setPromoteError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Sprint 2026-25 magic-link invite UI state.
  // inviteOpenId — ID того proto-tenant'а, у которого сейчас раскрыта inline
  //                форма «отправить инвайт». null = ни одна форма не открыта.
  // inviteForm   — содержимое формы (email + optional personal_message).
  // sendingId    — ID proto-tenant'а в процессе отправки (UX disable button).
  // inviteResult — результат последней отправки (показывается toast'ом):
  //                {protoId, email, link, email_sent}.
  const [inviteOpenId, setInviteOpenId] = useState<string | null>(null);
  const [inviteForm, setInviteForm] = useState<{ email: string; message: string }>({
    email: '',
    message: '',
  });
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<{
    protoId: string;
    email: string;
    link: string;
    email_sent: boolean;
  } | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const fetchProtos = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const list = await api.get<ProtoOut[]>('/api/v1/protos');
      setProtos(list);
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : 'Не удалось загрузить список';
      setListError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchProtos();
  }, [fetchProtos]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setCreateError('Имя обязательно');
      return;
    }
    setSubmitting(true);
    setCreateError(null);
    try {
      const body: Record<string, string> = { name: form.name.trim() };
      if (form.slug.trim()) body.slug = form.slug.trim();
      if (form.email_domain.trim()) body.email_domain = form.email_domain.trim();
      await api.post<ProtoOut>('/api/v1/protos', body);
      setForm(EMPTY_FORM);
      setShowForm(false);
      await fetchProtos();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : 'Не удалось создать';
      setCreateError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePromote = async (id: string) => {
    setPromotingId(id);
    setPromoteError(null);
    try {
      await api.post<ProtoOut>(`/api/v1/protos/${id}/promote`);
      await fetchProtos();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : 'Не удалось promote';
      setPromoteError(msg);
    } finally {
      setPromotingId(null);
    }
  };

  // Sprint 2026-25: real invite handler — POST /protos/{id}/invite,
  // backend creates auth.invitations row + sends email through Yandex SMTP.
  // Returns {invitation_id, email, link, email_sent}. Если email_sent=true —
  // показываем toast «отправлено»; если false — показываем link с copy
  // button (fallback при SMTP failure).
  const handleSendInvite = async (proto: ProtoOut) => {
    if (!inviteForm.email.trim()) {
      setInviteError('Email обязателен');
      return;
    }
    setSendingId(proto.id);
    setInviteError(null);
    setInviteResult(null);
    try {
      const body: Record<string, string> = { email: inviteForm.email.trim() };
      if (inviteForm.message.trim()) body.personal_message = inviteForm.message.trim();
      const result = await api.post<{
        invitation_id: string;
        email: string;
        link: string;
        email_sent: boolean;
        expires_at: string;
      }>(`/api/v1/protos/${proto.id}/invite`, body);
      setInviteResult({
        protoId: proto.id,
        email: result.email,
        link: result.link,
        email_sent: result.email_sent,
      });
      // Закрываем inline form, очищаем
      setInviteOpenId(null);
      setInviteForm({ email: '', message: '' });
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : 'Не удалось отправить';
      setInviteError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSendingId(null);
    }
  };

  // Fallback — если email не ушёл, юзер копирует link manually.
  const handleCopyLink = async (link: string, protoId: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(protoId);
      window.setTimeout(() => {
        setCopiedId((curr) => (curr === protoId ? null : curr));
      }, 2000);
    } catch {
      setPromoteError('Не удалось скопировать в буфер обмена');
    }
  };

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-4xl space-y-6 p-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Мои proto-tenants</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Кандидаты-фермеры, которых вы пригласили в ИИ-Колхоз. Создайте
              proto, скопируйте ссылку и отправьте фермеру.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            data-testid="toggle-create-form"
          >
            {showForm ? 'Скрыть форму' : 'Создать нового'}
          </Button>
        </header>

        {showForm && (
          <Card className="p-4">
            <form onSubmit={handleCreate} className="space-y-4" data-testid="create-form">
              <div className="space-y-1">
                <Label htmlFor="proto-name">Имя *</Label>
                <Input
                  id="proto-name"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Хозяйство «Восход»"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="proto-slug">Slug</Label>
                <Input
                  id="proto-slug"
                  value={form.slug}
                  onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                  placeholder="voskhod (необязательно)"
                />
                <p className="text-xs text-muted-foreground">
                  Оставьте пустым для авто-generation.
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor="proto-email-domain">Email domain</Label>
                <Input
                  id="proto-email-domain"
                  value={form.email_domain}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email_domain: e.target.value }))
                  }
                  placeholder="voskhod.ru (необязательно)"
                />
              </div>

              {createError && (
                <Alert variant="destructive">
                  <AlertDescription>{createError}</AlertDescription>
                </Alert>
              )}

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={submitting} data-testid="submit-create">
                  {submitting ? 'Создаём…' : 'Создать'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setForm(EMPTY_FORM);
                    setCreateError(null);
                  }}
                >
                  Очистить
                </Button>
              </div>
            </form>
          </Card>
        )}

        {promoteError && (
          <Alert variant="destructive">
            <AlertDescription>{promoteError}</AlertDescription>
          </Alert>
        )}

        {listError && (
          <Alert variant="destructive">
            <AlertDescription>{listError}</AlertDescription>
          </Alert>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground">Загружаем…</div>
        ) : protos.length === 0 ? (
          <Card className="p-6">
            <p className="text-sm text-muted-foreground" data-testid="empty-state">
              У вас ещё нет proto-tenants. Создайте первого через форму выше.
            </p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="protos-list">
            {protos.map((proto) => (
              <Card key={proto.id} className="p-4" data-testid={`proto-card-${proto.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-semibold">{proto.name}</span>
                      <StatusBadge status={proto.status} />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      slug: <span className="font-mono">{proto.slug}</span>
                    </div>
                    {proto.email_domain && (
                      <div className="text-xs text-muted-foreground">
                        email_domain:{' '}
                        <span className="font-mono">{proto.email_domain}</span>
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                      создан: {formatCreatedAt(proto.created_at)}
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setInviteOpenId((curr) => (curr === proto.id ? null : proto.id));
                        setInviteForm({ email: '', message: '' });
                        setInviteError(null);
                      }}
                      disabled={proto.status === 'active'}
                      data-testid={`invite-toggle-${proto.id}`}
                    >
                      {inviteOpenId === proto.id ? 'Отмена' : 'Отправить инвайт'}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handlePromote(proto.id)}
                      disabled={proto.status === 'active' || promotingId === proto.id}
                      data-testid={`promote-${proto.id}`}
                    >
                      {promotingId === proto.id
                        ? 'Promote…'
                        : proto.status === 'active'
                          ? 'Уже active'
                          : 'Promote вручную'}
                    </Button>
                  </div>
                </div>

                {/* Sprint 2026-25 inline invite form. Раскрывается при клике
                    на «Отправить инвайт». На submit POST /protos/{id}/invite —
                    backend создаёт invitation row + отправляет email. Result
                    показывается через inviteResult toast (вне Card). */}
                {inviteOpenId === proto.id && (
                  <div
                    className="mt-3 border-t border-border pt-3 space-y-2"
                    data-testid={`invite-form-${proto.id}`}
                  >
                    <Label htmlFor={`invite-email-${proto.id}`}>Email фермера</Label>
                    <Input
                      id={`invite-email-${proto.id}`}
                      type="email"
                      value={inviteForm.email}
                      onChange={(e) =>
                        setInviteForm((f) => ({ ...f, email: e.target.value }))
                      }
                      placeholder="farmer@example.com"
                      autoComplete="off"
                    />
                    <Label htmlFor={`invite-msg-${proto.id}`}>
                      Сообщение (необязательно)
                    </Label>
                    <Input
                      id={`invite-msg-${proto.id}`}
                      value={inviteForm.message}
                      onChange={(e) =>
                        setInviteForm((f) => ({ ...f, message: e.target.value }))
                      }
                      placeholder="Привет, перейди по ссылке…"
                    />
                    {inviteError && (
                      <Alert variant="destructive">
                        <AlertDescription>{inviteError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => handleSendInvite(proto)}
                        disabled={sendingId === proto.id}
                        data-testid={`invite-submit-${proto.id}`}
                      >
                        {sendingId === proto.id ? 'Отправляем…' : 'Отправить email'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Toast-результат: email_sent=true → success, false → fallback link. */}
                {inviteResult && inviteResult.protoId === proto.id && (
                  <Alert
                    className="mt-3"
                    variant={inviteResult.email_sent ? 'default' : 'destructive'}
                    data-testid={`invite-result-${proto.id}`}
                  >
                    <AlertDescription>
                      {inviteResult.email_sent ? (
                        <>✅ Приглашение отправлено на <b>{inviteResult.email}</b></>
                      ) : (
                        <div className="space-y-2">
                          <div>
                            ⚠ Email не доставлен (SMTP error). Скопируйте ссылку
                            и отправьте вручную фермеру:
                          </div>
                          <div className="flex items-center gap-2">
                            <code className="text-xs bg-background border border-border rounded px-2 py-1 break-all flex-1">
                              {inviteResult.link}
                            </code>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                handleCopyLink(inviteResult.link, proto.id)
                              }
                              data-testid={`copy-fallback-link-${proto.id}`}
                            >
                              {copiedId === proto.id ? 'Скопировано!' : 'Копировать'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
